/** Small genetics building blocks shared by the animal section, workspaces and job pages. */
import { Link } from "react-router";
import type { SubjectRef } from "../../../contract";
import { openRecord } from "../../../web/forms/EventForm";
import { formatDate } from "../../../web/lib/format";
import { getLanguage, t } from "../../../web/lib/i18n";
import { useApi } from "../../../web/lib/query";
import { registry } from "../../../web/registry";
import { Chip, Icon, cx } from "../../../web/ui/kit";
import { associationShort, formatEpd, percentileFill, type EvaluationSnapshot } from "../shared/epd";
import { CHECK_LABELS, JOB_STAGE_LABELS } from "../shared/labels";
import { DEFAULT_TRAITS, epdOrder, type TraitDefinition } from "../shared/traits";
import type { ReadinessCheck, TraitsDTO } from "../shared/types";

export function useTraits() {
  return useApi<TraitsDTO>("/m/genetics/traits", { staleTime: 5 * 60_000 });
}

/** A trait's name in the reader's language: built-ins through the catalog, ranch traits as written. */
export function traitName(key: string, traits?: TraitDefinition[]): string {
  const def = traits?.find((x) => x.key === key) ?? DEFAULT_TRAITS.find((x) => x.key === key);
  if (!def) return key;
  if (!DEFAULT_TRAITS.some((d) => d.key === key)) return getLanguage() === "es" && def.label_es ? def.label_es : def.label;
  return t(def.label);
}

export function PercentileBar({ pct, compact }: { pct: number | undefined; compact?: boolean }) {
  if (pct === undefined) return <span className="genetics-pbar-text">{t("No rank")}</span>;
  return (
    <span className="genetics-pbar" role="img" aria-label={t("Top {{pct}}%", { pct })}>
      <span className="genetics-pbar-track"><span className="genetics-pbar-fill" style={{ width: `${percentileFill(pct)}%` }} /></span>
      {!compact && <span className="genetics-pbar-text">{t("Top {{pct}}%", { pct })}</span>}
    </span>
  );
}

/** One run's EPDs: trait, value with accuracy, and a percentile bar. Only what the run reported; blanks stay blank. */
export function EpdGrid({ snapshot, only, traits }: { snapshot: EvaluationSnapshot; only?: string[]; traits?: TraitDefinition[] }) {
  const keys = epdOrder(Object.keys(snapshot.traits)).filter((k) => !only || only.includes(k));
  const idx = epdOrder(Object.keys(snapshot.indexes)).filter((k) => !only || only.includes(k));
  return (
    <div className="genetics-epd-grid">
      {[...keys.map((k) => ({ k, v: snapshot.traits[k] })), ...idx.map((k) => ({ k, v: snapshot.indexes[k] }))].map(({ k, v }) => (
        <div key={k} style={{ display: "contents" }}>
          <span className="genetics-epd-trait">{k}<small>{traitName(k, traits)}</small></span>
          <span className="genetics-epd-value">{formatEpd(v.value)}{v.acc !== undefined && <span className="genetics-epd-acc"> · {v.acc.toFixed(2)}</span>}</span>
          <PercentileBar pct={v.pct} />
        </div>
      ))}
    </div>
  );
}

export function RunLabel({ s }: { s: Pick<EvaluationSnapshot, "association" | "as_of" | "release" | "genomic" | "source"> }) {
  return (
    <span className="genetics-node-meta">
      <strong>{associationShort(s.association)}</strong>
      <span>{s.release ?? (s.as_of ? t("as of {{date}}", { date: formatDate(s.as_of) }) : t("Unknown date"))}</span>
      {s.genomic && <Chip tone="genetics" icon="dna">{t("Genomic-enhanced")}</Chip>}
      {s.source === "imported" && <Chip tone="neutral" icon="upload">{t("Imported")}</Chip>}
    </span>
  );
}

export function CompositionBar({ components, unknown }: { components: Array<{ breed: string; percent: number }>; unknown: number }) {
  return (
    <div>
      <div className="genetics-comp-bar" role="img" aria-label={components.map((c) => `${c.percent}% ${c.breed}`).join(", ")}>
        {components.map((c) => <span key={c.breed} style={{ width: `${c.percent}%` }} />)}
      </div>
      <div className="genetics-comp-legend">
        {components.map((c) => <span key={c.breed}><strong>{Number(c.percent.toFixed(2))}%</strong> {c.breed}</span>)}
        {unknown > 0 && <span className="muted">{t("{{percent}}% unknown", { percent: Number(unknown.toFixed(2)) })}</span>}
      </div>
    </div>
  );
}

export function StageSteps({ stages, current, status }: { stages: string[]; current: string | null; status?: string }) {
  const at = current ? stages.indexOf(current) : -1;
  return (
    <ol className="genetics-stages" aria-label={t("Stage")}>
      {stages.map((s, i) => (
        <li key={s} className={cx("genetics-stage", i < at && "is-done", i === at && (status === "done" ? "is-done" : "is-now"))} aria-current={i === at ? "step" : undefined}>
          {i < at || (i === at && status === "done") ? <Icon name="check" size={14} /> : null}{t(JOB_STAGE_LABELS[s] ?? s)}
        </li>
      ))}
    </ol>
  );
}

/** Open the record sheet for another module's type with only the fields that type actually has. */
export function openPrefilled(type: string, subjects: SubjectRef[], data: Record<string, unknown> = {}, opts: { job_id?: string; onDone?: () => void } = {}) {
  const def = registry.event(type);
  if (!def) return false;
  const known = Object.fromEntries(Object.entries(data).filter(([k, v]) => k in def.fields && v !== undefined && v !== null));
  const roles = new Set(def.subjects.map((s) => s.role));
  const subj = subjects.filter((s) => roles.has(s.role)).length ? subjects.filter((s) => roles.has(s.role))
    : subjects.map((s) => ({ ...s, role: def.subjects.find((x) => x.kind === s.kind)?.role ?? s.role }));
  openRecord({ initial: { type, subjects: subj, data: known, job_id: opts.job_id }, onDone: () => opts.onDone?.() });
  return true;
}

export function CheckChips({ checks, onFix, showOk = false }: { checks: ReadinessCheck[]; onFix?: (c: ReadinessCheck) => void; showOk?: boolean }) {
  const shown = checks.filter((c) => showOk || !c.ok);
  if (!shown.length) return <span className="genetics-check genetics-check-ok"><Icon name="check" size={14} />{t("Ready")}</span>;
  return (
    <div className="genetics-checks">
      {shown.map((c) => {
        const label = t(CHECK_LABELS[c.id] ?? c.id);
        const cls = cx("genetics-check", c.ok ? "genetics-check-ok" : "genetics-check-bad");
        const body = <><Icon name={c.ok ? "check" : "warn"} size={14} />{c.ok ? label : t("Missing: {{what}}", { what: label.toLowerCase() })}</>;
        return !c.ok && onFix && c.fix ? <button key={c.id} type="button" className={cls} onClick={() => onFix(c)}>{body}</button> : <span key={c.id} className={cls}>{body}</span>;
      })}
    </div>
  );
}

export function AnimalLink({ id, label, tag }: { id: string; label: string; tag?: string | null }) {
  return <Link to={`/cattle/${id}`} className={tag ? "mono" : undefined}>{label}</Link>;
}

export const fileUrl = (id: string) => `/api/m/genetics/files/${id}`;

/** Read a picked file as text in the browser (CSV exports from labs and associations). */
export function readFileText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result ?? ""));
    r.onerror = () => reject(r.error);
    r.readAsText(file);
  });
}
